import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import {
	FileDown,
	Headphones,
	LoaderCircle,
	Home,
	AArrowDown,
	AArrowUp,
	Globe,
	Download,
	Share,
	Image,
	Smartphone,
	Info,
	FileText,
	BookOpen,
	LogOut,
	MonitorDown,
	RefreshCw,
	ListOrdered,
	ChartColumn,
    Star,
} from 'lucide-react';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import config from '@/config';
import { Article } from '@/constants';
import { AppContext } from '@/app.context';
import { contentOf, track } from '@shared/events';
import { setIssueStatsOpen } from '../stats/stats-drawer-store';
import { LinkItem } from '@shared/config';
import { useInstallAction } from '@features/install-pwa';

import { IssueContext, collectIssueImages } from '@entities/issue';
import { addFavorite, removeFavorite, useIsFavorite } from '@entities/favorite';
import {
	saveDownloadedIssue,
	removeDownloadedIssue,
	useIsIssueDownloaded,
	confirmRemoveDownloadedIssue,
	refreshDownloadedIssue,
	DOWNLOADED_ISSUE_BASE_PATH,
} from '@entities/downloaded-issue';
import { ReaderStateContext } from '@widgets/issue-reader/model/reader-state-context';
import { ReplicaContext } from '@widgets/issue-reader/replica/replica.context';
// Deep-imported rather than through `@entities/audio-player`'s barrel --
// same pre-existing, tolerated `fsd/no-public-api-sidestep` pattern
// `widgets/issue-reader/responsive/responsive.tsx`'s own `PlayerButton`
// (this component's topbar predecessor) already uses, for the same reason:
// the barrel also re-exports `BasicPlayer`, and a static import through it
// would defeat that module's own lazy-loading.
import { AudioPlayerContext } from '@entities/audio-player/model/audio-player.context';
import { Logger } from '@shared/lib/logger';

import styles from './menu-button.module.css';
import {
	useDialog,
	requestPersistentStorage,
	useServiceWorker,
	FONT_SIZE_STEP,
	MIN_FONT_SIZE,
	MAX_FONT_SIZE,
} from '@shared/lib/hooks';
import { translate, useT } from '@shared/i18n';

const logger = new Logger('menu-button');


export class IssueButton {
	static TableOfContents = TableOfContentsButton;
	static ShareArticle = ShareArticleButton;
	static Replica = ReplicaButton;
	static SignOut = SignOutButton;
	static Links = Links;
	static Library = Library;
	static Responsive = ResponsiveButton;
	static SinglePage = SinglePageButton;
	static DoublePage = DoublePageButton;
	static Favorite = FavoriteButton;
	static DownloadIssue = DownloadIssueButton;
	static RefreshContent = RefreshContentButton;
	static Font = FontSizeButton;
	static WebsiteButton = WebsiteButton;
	static Legal = Legal;
	static Install = InstallButton;
	static About = AboutButton;
	static Language = Language;
	static Audio = Audio;
	static DownloadPdf = DownloadPdfButton;
	static Stats = StatsButton;
}

function Links({ links }: LinkArgs) {
	if (!links || !links.length) {
		return null;
	}
	return (
		<section className={styles['links']}>
			{links
				.filter((l) => l.title && l.url)
				.map((l, i) => {
					const { url, title } = l;
					return (
						<a key={i} href={url} target="_blank" rel="noreferrer">
							<h4 dir="auto">{title}</h4>
							<div></div>
						</a>
					);
				})}
		</section>
	);
}

interface LinkArgs {
	links: Array<LinkItem> | undefined;
}

// The one ToC trigger that's always there -- `IssueCover` is hidden while an
// article's audio player takes its corner, and Shift+T needs a keyboard.
function TableOfContentsButton({ onClick }: { onClick: () => void }) {
	const t = useT();
	return (
		<button type={'button'} title={t('reader.toc')} onClick={onClick}>
			<span>{t('reader.toc')}</span>
			<span>
				<ListOrdered />
			</span>
		</button>
	);
}

function Library() {
	const t = useT();
	const navigate = useNavigate();
	return (
		<button
			type={'button'}
			onClick={() => {
				navigate({
					to: '/',
				});
			}}
		>
			<span>{t('reader.library')}</span>
			<span>
				<Home />
			</span>
		</button>
	);
}

// `issue.externalUrl` is the issue's PDF, hosted outside this app
// (`admin.canvasflow.io`). Browsers ignore `<a download>` on a cross-origin
// URL and open the file in a new tab instead, so this fetches the PDF (the
// host allows CORS) and saves it from a same-origin blob URL, where
// `download` is honoured -- a real download, not a navigation. If the
// download fails (offline, an HTTP error, a host without CORS), the PDF is
// opened in a new tab instead -- see `openPdfInNewTab`.
function DownloadPdfButton({ url }: DownloadPdfProps) {
	const t = useT();
	const [isDownloading, setIsDownloading] = useState(false);

	const onClick = () => {
		if (isDownloading) {
			return;
		}
		const fileName = url.split('/').pop()?.split('?')[0] || 'file.pdf';
		setIsDownloading(true);
		fetch(url)
			.then((res) => {
				if (!res.ok) {
					throw new Error(`HTTP ${res.status}`);
				}
				return res.blob();
			})
			.then((blob) => {
				logger.debug('Downloaded the PDF: ', { fileName });
				saveBlob(blob, fileName);
			})
			.catch((error: unknown) => {
				logger.error('Failed to download the PDF, opening it instead', error);
				openPdfInNewTab(url);
			})
			.finally(() => setIsDownloading(false));
	};

	return (
		<button type={'button'} onClick={onClick} disabled={isDownloading}>
			<span>{t('reader.downloadPdf')}</span>
			<span>
				<FileDown size={24} />
			</span>
		</button>
	);
}

interface DownloadPdfProps {
	url: string;
}

// The fallback runs after the fetch settles, so the click's user activation
// may have expired by then (a slow failure mid-download) and the browser
// blocks the popup. `window.open` returns null in that case, so the toast
// offers a button instead -- tapping it is a fresh click, which a popup
// blocker always allows. (Not opened with the `noopener` feature: that makes
// `window.open` always return null, hiding whether it was blocked -- the
// opener link is cut by hand instead.)
function openPdfInNewTab(url: string) {
	const open = () => {
		const tab = window.open(url, '_blank');
		if (tab) {
			tab.opener = null;
		}
		return tab;
	};
	if (open()) {
		return;
	}
	toast.error(translate('reader.pdfFailed'), {
		action: { label: translate('reader.openPdf'), onClick: () => open() },
	});
}

function SignOutButton({ onClick }: SignOutProps) {
	const t = useT();
	const router = useRouter();
	const basename = `${router.basepath}/logout`;

	return (
		<button
			type={'button'}
			onClick={() => {
				onClick(basename);
			}}
		>
			<span>{t('reader.logOut')}</span>
			<span>
				<LogOut />
			</span>
		</button>
	);
}

interface SignOutProps {
	onClick: (url?: string) => void;
}

function Legal() {
	const t = useT();
	const components = [];
	const { title } = config.build;
	const { legal } = config.app;
	components.push(
		<div className="optanon-toggle-display">
			{t('reader.legal.copyright', { title })}
		</div>,
	);
	if (legal?.tou) {
		components.push(
			<a href={legal.tou.url} target="_blank" rel="noreferrer">
				{legal.tou.label}
			</a>,
		);
	}
	if (legal?.privacy) {
		components.push(
			<a href={legal.privacy.url} target="_blank" rel="noreferrer">
				{legal.privacy.label}
			</a>,
		);
	}
	components.push(<div className="optanon-toggle-display">{t('reader.legal.doNotSell')}</div>);
	if (legal?.cookie) {
		components.push(
			<a href={legal.cookie.url} target="_blank" rel="noreferrer">
				{legal.cookie.label}
			</a>,
		);
	}
	return (
		<div className={styles['legal']}>
			<div>{components}</div>
		</div>
	);
}

function FontSizeButton({
	fontSize,
	setFontSize,
}: {
	fontSize: number;
	setFontSize: (size: number) => void;
}) {
	const t = useT();
	const animations = {
		hover: {
			scale: 1.1,
		},
		tap: {
			scale: 0.8,
		},
	};
	return (
		<div className={styles['font-button']}>
			<DecreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
			<span>{t('reader.textSize')}</span>
			<IncreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
		</div>
	);
}

function DecreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const t = useT();
	const title = t('reader.decreaseFont');

	const onClick = () => {
		const newFontSize = fontSize - FONT_SIZE_STEP;
		setFontSize(newFontSize <= MIN_FONT_SIZE ? MIN_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MIN_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<AArrowDown />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<AArrowDown />
		</motion.button>
	);
}

function IncreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const t = useT();
	const title = t('reader.increaseFont');

	const onClick = () => {
		const newFontSize = fontSize + FONT_SIZE_STEP;
		setFontSize(newFontSize >= MAX_FONT_SIZE ? MAX_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MAX_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<AArrowUp />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<AArrowUp />
		</motion.button>
	);
}

interface FontButton {
	fontSize: number;
	setFontSize: (size: number) => void;
	animations: {
		hover: {
			scale: number;
		};
		tap: {
			scale: number;
		};
	};
}

function WebsiteButton(args: ExternalUrlButtonProps) {
	const { url, title } = args;
	if (!url || !title) {
		return null;
	}

	return (
		<a href={url} target="_blank" rel="noreferrer">
			<span dir="auto">{title}</span>
			<span>
				<Globe />
			</span>
		</a>
	);
}

export function DownloadButton(args: ExternalUrlButtonProps) {
	const t = useT();
	const url: string = args.url || '';
	const [isDownloading, setIsDownloading] = useState(false);
	const onClick = () => {
		const fileName: string = url.split('/').pop() || '';
		setIsDownloading(true);
		fetch(url)
			.then((res) => res.blob())
			.then((blob) => {
				logger.debug(`Downloaded the file: `, {
					fileName,
				});
				saveBlob(blob, fileName);
			})
			.catch((error: unknown) => {
				logger.error('Failed to download the file', error);
				toast.error(t('reader.downloadFailed'));
			})
			.finally(() => {
				setIsDownloading(false);
			});
	};
	if (isDownloading) {
		return (
			<button type={'button'} title={t('reader.downloading')} disabled>
				<span>{t('reader.downloading')}</span>
				<span>
					<Download />
				</span>
			</button>
		);
	}

	return (
		<button type={'button'} title={t('reader.download')} onClick={onClick}>
			<span>{t('reader.download')}</span>
			<span>
				<Download />
			</span>
		</button>
	);
}

interface ExternalUrlButtonProps {
	url?: string;
	title?: string;
}

function ShareArticleButton({
	article,
	language,
	onShare,
}: ShareArticleButtonProps) {
	const t = useT();
	if (!article) return null;
	const title: string | undefined = article.name?.[language];
	const url: string = window.location.href;
	const message = t('reader.linkCopied');
	const onClick = () => {
		if (navigator.share) {
			navigator
				.share({
					title,
					url,
				})
				.then(() => {
					onShare(article);
					logger.debug('Shared url:', {
						title,
						url,
					});
				})
				.catch((error: unknown) => {
					logger.error('Failed to share url', error);
				});
			return;
		}

		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(url)
				.then(
					() => {
						onShare(article, 'clipboard');
						toast.success(message);
					},
					() => {
						toast.error(t('reader.linkCopyFailed'));
					},
				)
				.catch((error: unknown) => {
					logger.error('Failed to copy url to clipboard', error);
				});
			return;
		}

		const inputElement = document.createElement('input');
		document.body.appendChild(inputElement);
		inputElement.value = url;
		inputElement.select();
		document.execCommand('copy');
		document.body.removeChild(inputElement);
		onShare(article, 'clipboard');
		toast.success(message);
	};
	return (
		<button type={'button'} title={t('reader.share')} onClick={onClick}>
			<span>{t('reader.share')}</span>
			<span>
				<Share />
			</span>
		</button>
	);
}

interface ShareArticleButtonProps {
	article: Article | null;
	language: string;
	onShare: (article: Article, method?: 'os' | 'clipboard') => void;
}

function ReplicaButton(props: ReplicaButtonProps) {
	const t = useT();
	const { onlineStatus } = useContext(AppContext);
	if (config.issue.contentType === 'responsive') {
		return null;
	}
	// Only reachable from the responsive view (this is its one call site),
	// so "offline" is the only condition that matters here -- replica pages
	// are downloaded on demand when switching into that mode, which has
	// nothing to fetch from while offline.
	if (!onlineStatus) {
		return (
			<button
				type={'button'}
				title={t('reader.magazineOffline')}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<span>{t('reader.magazineView')}</span>
				<span>
					<Image />
				</span>
			</button>
		);
	}
	return (
		<button type={'button'} title={t('reader.magazine')} onClick={props.onClick}>
			<span>{t('reader.magazineView')}</span>
			<span>
				<Image />
			</span>
		</button>
	);
}

interface ReplicaButtonProps {
	onClick: () => void;
}

/**
 * Opens the reader's Stats drawer: the reader's own figures for this issue.
 * `onOpen` closes the menu it sits in, like the Settings entry.
 */
function StatsButton({ onOpen }: { onOpen?: () => void }) {
	const t = useT();
	return (
		<button
			type={'button'}
			onClick={() => {
				onOpen?.();
				setIssueStatsOpen(true);
			}}
		>
			<span>{t('stats.title')}</span>
			<span>
				<ChartColumn />
			</span>
		</button>
	);
}

function FavoriteButton({ article }: FavoriteButtonProps) {
	const t = useT();
	const { issue, language } = useContext(IssueContext);
	// The favorite's identity: the online reader's own URL for this article,
	// built the same way `pages/issue`'s `useArticle` navigates to it.
	const path =
		issue && article
			? `/issues/${issue.slug || issue.id}/${article.slug || article.id}`
			: null;
	const isSelected = useIsFavorite(path);

	if (!config.app.favorite || article?.role === 'advert') {
		return null;
	}

	if (!article || !issue || !path) {
		return null;
	}

	const onClick = () => {
		if (isSelected) {
			removeFavorite(path);
		} else {
			addFavorite({
				path,
				title: article.name?.[language] || article.slug || article.id,
				description: article.description || undefined,
				issueTitle: issue.name,
			});
		}
		if (!isSelected) {
			track('favorite_add', {}, contentOf({ issue, article, language }));
		}
	};

	return (
		<button
			type={'button'}
			title={isSelected ? t('reader.unfavorite') : t('reader.favorite')}
			aria-pressed={isSelected}
			onClick={onClick}
		>
			<span>{t('reader.favorite')}</span>
			<span>
				<Star
					fill="currentColor"
					className={cn(
						styles['fillable-icon'],
						isSelected && styles['fillable-icon-active'],
					)}
				/>
			</span>
		</button>
	);
}

interface FavoriteButtonProps {
	article: Article | null;
}

// Issue-level, not article-level (unlike `Favorite` above) -- reads
// `IssueContext`'s `issue`/`articles` directly rather than taking props,
// same self-contained shape as `Library`/`ResponsiveButton` below. Gated
// behind `config.app.downloadedIssues`, the same tenant opt-in `AppMenu`'s
// Downloads entry uses -- this is that feature's other half, the actual
// write path the downloads drawer reads back from.
function DownloadIssueButton() {
	const t = useT();
	const { db } = useContext(AppContext);
	const { issue, articles, stylesMap } = useContext(IssueContext);
	const { pinIssueAssets, unpinIssueAssets } = useServiceWorker();
	const [isDownloaded, setIsDownloaded] = useIsIssueDownloaded(issue?.id);
	const [isSaving, setIsSaving] = useState(false);
	// The article-list query variant in effect right now (`pages/issue/
	// model/issue.hooks.ts`'s `IssueSearch`, read loosely -- this widget
	// can't import a `pages/`-layer type) -- persisted with the download so
	// "refresh content" (`RefreshContentButton` below) replays the exact
	// same query later instead of guessing at default pagination/sort.
	const search = useSearch({ strict: false }) as {
		limit?: number;
		sortKey?: string;
		reverse?: boolean;
	};

	if (!config?.app?.downloadedIssues?.enabled || !issue) {
		return null;
	}

	const removeDownload = () => {
		if (!db || isSaving) {
			return;
		}
		setIsSaving(true);
		removeDownloadedIssue(db, issue.id)
			.then(() => {
				setIsDownloaded(false);
				logger.debug(`Removed downloaded issue: `, issue.id);
				toast.success(t('downloadedIssue.removed'));
				// Keeps every cached image byte in place -- only clears the
				// pin so they're no longer exempt from storage-pressure
				// eviction. See PIN_ISSUE_ASSETS/UNPIN_ISSUE_ASSETS's own
				// doc comments (shared/sw/sw.context.tsx).
				unpinIssueAssets(issue.id);
			})
			.catch((error: unknown) => {
				logger.error('Failed to remove downloaded issue', error);
				toast.error(t('downloadedIssue.removeFailed'));
			})
			.finally(() => setIsSaving(false));
	};

	const onClick = () => {
		if (!db || isSaving) {
			return;
		}

		if (isDownloaded) {
			confirmRemoveDownloadedIssue(removeDownload);
			return;
		}

		setIsSaving(true);
		// Downloading is the reader's explicit "keep this for me offline"
		// action, so it's the right moment to ask for persistent storage --
		// contextual and far likelier to be granted than at app boot (see
		// `usePersistentStorage`'s doc comment; Firefox prompts).
		void requestPersistentStorage();
		// `stylesMap` is exactly the style set this issue is rendering with
		// right now (inheritance already resolved) -- stored alongside so the
		// offline reader renders it identically.
		saveDownloadedIssue(db, issue, articles, {
			styles: [...stylesMap.values()],
			downloadQuery: {
				limit: search.limit,
				sortKey: search.sortKey,
				reverse: search.reverse,
			},
		})
			.then(() => {
				setIsDownloaded(true);
				logger.debug(`Downloaded issue: `, issue.id);
				track('issue_download', {}, contentOf({ issue }));
				toast.success(t('reader.downloaded'));
				// Guarantees every responsive-content image this issue
				// needs is cached and exempt from storage-pressure eviction
				// -- most are already sitting in cache from
				// useDownloadAssets's incidental background prefetch (the
				// moment this issue was opened), this just fetches whatever
				// isn't and pins all of it. Replica pages are deliberately
				// excluded -- collectIssueImages only ever reads responsive
				// `article.components`, never `issue.pages`/`article.
				// replica` (see saveDownloadedIssue's own doc comment).
				pinIssueAssets(collectIssueImages(articles), issue.id);
			})
			.catch((error: unknown) => {
				logger.error('Failed to download issue', error);
				toast.error(t('reader.downloadIssueFailed'));
			})
			.finally(() => setIsSaving(false));
	};

	return (
		<button
			type={'button'}
			title={isDownloaded ? t('reader.removeDownloaded') : t('reader.downloadIssue')}
			onClick={onClick}
			disabled={isSaving}
		>
			<span>{isDownloaded ? t('reader.removeDownloaded') : t('reader.downloadIssue')}</span>
			<span>
				<Download
					fill="currentColor"
					className={cn(
						styles['fillable-icon'],
						isDownloaded && styles['fillable-icon-active'],
					)}
				/>
			</span>
		</button>
	);
}

// Only meaningful for the Dexie-backed reader (`basePath ===
// DOWNLOADED_ISSUE_BASE_PATH`) -- the online reader's content is already
// live, so its menu never renders this (gated at each template's menu JSX
// call site, not here, matching how `DownloadIssueButton` above is gated by
// `config.app.downloadedIssues.enabled` rather than by route).
function RefreshContentButton() {
	const t = useT();
	const { db, onlineStatus } = useContext(AppContext);
	const { issue, articles, basePath, reloadIssue } = useContext(IssueContext);
	const { pinIssueAssets, pruneRemovedIssueAssets } = useServiceWorker();
	const queryClient = useQueryClient();
	const [isRefreshing, setIsRefreshing] = useState(false);

	if (basePath !== DOWNLOADED_ISSUE_BASE_PATH || !issue) {
		return null;
	}

	const onClick = () => {
		if (!db || isRefreshing || !onlineStatus) {
			return;
		}
		setIsRefreshing(true);
		const previousImages = new Set(collectIssueImages(articles));
		const toastId = toast.loading(t('reader.refreshing'));
		refreshDownloadedIssue({
			db,
			issueId: issue.id,
			downloadQuery: issue.downloadQuery,
			queryClient,
		})
			.then((freshIssue) => {
				const freshImages = collectIssueImages(
					freshIssue.articles ?? [],
				);
				const freshImagesSet = new Set(freshImages);
				const newImages = freshImages.filter(
					(url) => !previousImages.has(url),
				);
				const removedImages = [...previousImages].filter(
					(url) => !freshImagesSet.has(url),
				);
				if (newImages.length) {
					pinIssueAssets(newImages, issue.id);
				}
				if (removedImages.length) {
					pruneRemovedIssueAssets(removedImages, issue.id);
				}
				logger.debug(`Refreshed downloaded issue: `, issue.id);
				toast.success(t('reader.refreshed'), { id: toastId });
				reloadIssue?.();
			})
			.catch((error: unknown) => {
				logger.error('Failed to refresh downloaded issue', error);
				toast.error(t('reader.refreshFailed'), { id: toastId });
			})
			.finally(() => setIsRefreshing(false));
	};

	if (!onlineStatus) {
		return (
			<button
				type={'button'}
				title={t('reader.refreshOffline')}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<span>{t('reader.refresh')}</span>
				<span>
					<RefreshCw />
				</span>
			</button>
		);
	}

	return (
		<button
			type={'button'}
			title={t('reader.refresh')}
			onClick={onClick}
			disabled={isRefreshing}
		>
			<span>{t('reader.refresh')}</span>
			<span>
				<RefreshCw
					className={isRefreshing ? styles['spin'] : undefined}
				/>
			</span>
		</button>
	);
}

function ResponsiveButton() {
	const t = useT();
	const { currentArticle, issue, language } = useContext(IssueContext);
	const { setMode } = useContext(ReaderStateContext);
	if (config.issue.contentType === 'replica') {
		return null;
	}
	const onClick = () => {
		setMode('responsive');
		if (issue) {
			track(
				'layout_change',
				{ to: 'responsive' },
				contentOf({
					issue,
					article: currentArticle,
					language,
					contentType: 'replica',
				}),
			);
		}
	};
	return (
		<button type={'button'} title={t('reader.digital')} onClick={onClick}>
			<span>{t('reader.digitalView')}</span>
			<span>
				<Smartphone />
			</span>
		</button>
	);
}

function AboutButton() {
	const t = useT();
	const { setShowDialog } = useDialog();

	const onClick = () => {
		setShowDialog(true);
	};
	return (
		<button type={'button'} title={t('reader.info')} onClick={onClick}>
			<span>{t('common.about')}</span>
			<span>
				<Info />
			</span>
		</button>
	);
}

// Reuses `useInstallAction` (`@features/install-pwa`) -- the same gating +
// native-prompt-vs-`InstallationDialog` fallback the home screen's
// `AppMenu` entry uses, toggling the same app-root-mounted dialog
// singleton, so nothing reader-specific needs to be mounted here.
function InstallButton() {
	const t = useT();
	const { show, onClick } = useInstallAction();

	if (!show) {
		return null;
	}

	return (
		<button type={'button'} title={t('common.installApp')} onClick={onClick}>
			<span>{t('common.installApp')}</span>
			<span>
				<MonitorDown />
			</span>
		</button>
	);
}

function Language({ language, options, onSelectLanguage }: LanguageProps) {
	const t = useT();
	const { issue, currentArticle } = useContext(IssueContext);

	const [value, setValue] = useState(language);

	if (!options.length || options.length < 2) {
		return null;
	}

	useEffect(() => {
		if (!language) {
			return;
		}
		setValue(language);

	}, [language]);

	const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const { value } = e.target;
		setValue(value);

		const selectedOption = options.filter((o) => {
			if (o.value === value) {
				return true;
			}
			return false;
		});
		if (!selectedOption.length) {
			return;
		}
		onSelectLanguage(selectedOption[0]);
		// The reader's own change, not the language an issue resolved to.
		if (issue) {
			track(
				'language_change',
				{ to: value, scope: 'issue' },
				contentOf({ issue, article: currentArticle, language: value }),
			);
		}
	};

	return (
		<div className={styles['language']}>
			<span>{t('reader.language')}</span>
			<span>
				<select
					value={value}
					name={'Language'}
					title={t('reader.selectLanguage')}
					onChange={onChange}
				>
					{options.map((o, i) => {
						const { name, value } = o;
						if (o.disabled) {
							return (
								<option dir="auto" key={i} value={value} disabled>
									{name}
								</option>
							);
						}

						return (
							<option dir="auto" key={i} value={value}>
								{name}
							</option>
						);
					})}
				</select>
			</span>
		</div>
	);
}

interface LanguageProps {
	language: string;
	options: Array<LanguageOption>;
	onSelectLanguage: (e: LanguageOption) => void;
}

export interface LanguageOption {
	value: string;
	name: string;
	disabled?: boolean;
}

// Self-contained (reads context directly) rather than prop-driven, matching
// `IssueButton.Favorite`/`.Library`/`.About`'s own pattern -- moved here
// 2026-09-14 from a topbar icon (`responsive.tsx`'s `PlayerButton`, now
// removed) into the menu, same trigger behavior verbatim (same four
// `AudioPlayerContext` setters + `setAudioPlayerUrl` + `play()`/`pause()`),
// same "only when this article actually has audio in the current language"
// guard -- just a different entry point, per user direction. `play()`/
// `pause()` are called synchronously, not after a delay -- see `onClick`'s
// own comment below for why, and for why it's a choice between the two.
const ARTWORK_SIZES = [
	'96x96',
	'128x128',
	'192x192',
	'256x256',
	'384x384',
	'512x512',
];

function Audio() {
	const t = useT();
	const {
		isReady,
		url: currentUrl,
		setAlbum,
		setTitle,
		setArtwork,
		setSubject,
	} = useContext(AudioPlayerContext);
	const { onlineStatus } = useContext(AppContext);
	const { language, currentArticle, issue } = useContext(IssueContext);
	const { audioPlayerUrl, setAudioPlayerUrl } =
		useContext(ReaderStateContext);

	if (!currentArticle || !currentArticle.audios) {
		return null;
	}

	if (!currentArticle.audios.length || !language) {
		return null;
	}

	const audio = currentArticle.audios
		?.filter((a) => a.lang === language)
		.pop();

	if (!audio) {
		return null;
	}

	const { name, thumbnail, featureImage } = currentArticle;
	const title = name?.[language];
	// Same `thumbnail ?? featureImage` cover-art fallback every other reader
	// of `Article` uses (`games.tsx`, both `feed-issue.tsx` templates) --
	// an article with no `thumbnail` still has real cover art via
	// `featureImage`, so media-session artwork shouldn't go without one
	// just because this call site used a narrower check.
	const artwork = thumbnail ?? featureImage;
	// A single `MediaImage` -- what this used to send -- confirmed live
	// (real iPhone, 2026-09-15) to leave the lock screen showing nothing at
	// all for title/artist/album/artwork together, not just a missing
	// cover. iOS is documented to pick a single `sizes` entry per surface
	// (the compact lock-screen widget wants something small, ~96-128px;
	// the expanded view wants larger) and show nothing on a surface with
	// no matching entry -- there's only one real image here, no
	// server-resized variants, so the same URL is repeated under several
	// plausible sizes and left for iOS to pick from, rather than a single
	// declared size that might not match what either surface wants.
	const artworkImages = artwork
		? ARTWORK_SIZES.map((sizes) => ({ src: artwork, sizes }))
		: undefined;

	const { url } = audio;

	// This article's audio is the one the reader asked for (`audioPlayerUrl`
	// changes on the click itself; the player's own `url` follows a render
	// later). It is in the dock once the player reports it ready, and still
	// loading until then -- a failed load clears `audioPlayerUrl`, which
	// re-enables the button for a retry.
	const isRequested = audioPlayerUrl === url;
	const isInDock = isRequested && currentUrl === url && isReady;
	const isLoading = isRequested && !isInDock;

	const onClick = () => {
		// Already in the dock: playback is controlled from the player.
		if (isInDock) return;
		// Audio is streamed, never part of a downloaded issue, so there is
		// nothing to try while offline.
		if (!onlineStatus) {
			toast.error(t('player.offline'));
			return;
		}
		// Only hands the player its track. The player loads it, opens the
		// dock once it knows the track plays, and leaves it paused: every
		// start is a direct tap on the player's own play button, which is
		// also what browsers' autoplay rules want (a `play()` issued here
		// would land after an async load, where some of them silently refuse
		// it). Setting a different url while another article is loading or
		// playing replaces it.
		// The article this track belongs to, for the audio events: the player
		// keeps playing after the reader has moved on to another one.
		setSubject(contentOf({ issue, article: currentArticle, language }));
		setAudioPlayerUrl(url);
		// Only `title` (article title) and `album` (issue name) are wanted
		// on the lock screen. `artist` used to carry the SPA's name, but
		// iOS is documented to hide `album` entirely on the lock screen
		// whenever `artist` has a value at all -- since the issue name
		// mattered more than the app name there, `artist` was removed from
		// `AudioPlayerContext` entirely rather than just left unset here.
		setAlbum(issue?.name);
		setTitle(title);
		setArtwork(artworkImages);
	};

	return (
		<button
			type="button"
			title={t('reader.playArticle')}
			onClick={onClick}
			disabled={isLoading}
			aria-busy={isLoading}
		>
			<span>{t('reader.playArticle')}</span>
			<span>
				{isLoading ? (
					<LoaderCircle size={24} className={styles['spin']} />
				) : (
					<Headphones size={24} />
				)}
			</span>
		</button>
	);
}

function SinglePageButton() {
	const t = useT();
	const { setViewMode } = useContext(ReplicaContext);
	const onClick = () => {
		setViewMode('single');
	};

	return (
		<button type={'button'} title={t('reader.singlePage')} onClick={onClick}>
			<span>{t('reader.singlePageView')}</span>
			<span>
				<FileText />
			</span>
		</button>
	);
}

function DoublePageButton() {
	const t = useT();
	const { setViewMode } = useContext(ReplicaContext);
	const onClick = () => {
		setViewMode('double');
	};
	return (
		<button type={'button'} title={t('reader.doublePage')} onClick={onClick}>
			<span>{t('reader.doublePageView')}</span>
			<span>
				<BookOpen />
			</span>
		</button>
	);
}

const saveBlob = (function () {
	const a = document.createElement('a');
	document.body.appendChild(a);
	return function (blob: Blob, fileName: string) {
		const url = window.URL.createObjectURL(blob);
		a.href = url;
		a.download = fileName;
		a.click();
		// Deferred: revoking in the same task as `click()` can cancel the
		// download in Safari before it has read the blob.
		setTimeout(() => window.URL.revokeObjectURL(url), 1000);
	};
})();
